import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  MapPin, Image as ImageIcon, Loader2,
  ChevronLeft, X, CheckCircle2, Search
} from "lucide-react";
import API from "../api";
import GoogleMapComponent from '../components/GoogleMapComponent';

const CATEGORIES = [
  { value: "temple",       label: "🛕 Temple" },
  { value: "trek",         label: "🥾 Trek" },
  { value: "lake",         label: "🏞️ Lake" },
  { value: "waterfall",    label: "💧 Waterfall" },
  { value: "hill_station", label: "⛰️ Hill Station" },
  { value: "wildlife",     label: "🐾 Wildlife" },
  { value: "camping",      label: "⛺ Camping" },
  { value: "village",      label: "🏡 Village" },
  { value: "historical",   label: "🏛️ Historical" },
  { value: "other",        label: "📍 Other" },
];

const DISTRICTS = [
  "Dehradun", "Haridwar", "Tehri Garhwal", "Pauri Garhwal", "Chamoli",
  "Rudraprayag", "Uttarkashi", "Nainital", "Almora", "Bageshwar",
  "Pithoragarh", "Champawat", "Udham Singh Nagar"
];

const DISTRICT_COORDS = {
  "Dehradun": { lat: 30.3165, lng: 78.0322 },
  "Haridwar": { lat: 29.9457, lng: 78.1642 },
  "Tehri Garhwal": { lat: 30.3872, lng: 78.4817 },
  "Pauri Garhwal": { lat: 30.1460, lng: 78.7691 },
  "Chamoli": { lat: 30.2738, lng: 79.3262 },
  "Rudraprayag": { lat: 30.2844, lng: 78.9811 },
  "Uttarkashi": { lat: 30.7268, lng: 78.4354 },
  "Nainital": { lat: 29.3919, lng: 79.4542 },
  "Almora": { lat: 29.5892, lng: 79.6467 },
  "Bageshwar": { lat: 29.8377, lng: 79.7715 },
  "Pithoragarh": { lat: 29.5829, lng: 80.2182 },
  "Champawat": { lat: 29.3350, lng: 80.0934 },
  "Udham Singh Nagar": { lat: 28.9818, lng: 79.4087 }
};

// Map configuration replaced by GoogleMapComponent

export default function AddPlace() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "", description: "", category: "temple", address: "",
  });
  const [marker, setMarker]         = useState(null);
  const [image, setImage]           = useState(null);
  const [preview, setPreview]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [dragOver, setDragOver]     = useState(false);
  const fileRef                     = useRef();
  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));


  const handleImage = (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Please select a valid image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImage(null); setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadImage = async () => {
    if (!image) return null;
    const formData = new FormData();
    formData.append("file", image);
    const token = localStorage.getItem("token");
    const { data } = await API.post("/upload", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
    if (data.url && /^https?:\/\//i.test(data.url)) {
      return data.url;
    }
    const base = process.env.REACT_APP_BACKEND_URL || "https://uttarakhand-travel-app-123.onrender.com";
    return `${base}${data.url || data.path}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Place name is required");
    if (form.name.trim().length < 4) return toast.error("Name must be at least 4 characters long");
    if (!/[aeiouAEIOU]/.test(form.name)) return toast.error("Name cannot be all consonants");
    if (!form.category)    return toast.error("Please select a category");
    if (!form.address)     return toast.error("Please select a district");
    if (!marker)           return toast.error("Please search and select a location");

    setLoading(true);
    try {
      const imageUrl = await uploadImage();
      const payload = {
        name:        form.name.trim(),
        description: form.description.trim() || " ",
        category:    form.category,
        address:     form.address.trim(),
        latitude:    marker.lat,
        longitude:   marker.lng,
        images:      imageUrl ? [imageUrl] : [],
      };
      await API.post("/places", payload);
      toast.success("Place added! Visible after admin approval ✅");
      navigate("/places");
    } catch (err) {
      console.error(err);
      const d = err.response?.data;
      const message =
        typeof d?.detail === "string"   ? d.detail :
        Array.isArray(d?.detail)        ? d.detail.map((e) => e.msg).join(", ") :
        typeof d?.message === "string"  ? d.message :
        typeof err.message === "string" ? err.message :
        "Failed to add place";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto p-6 mt-10 bg-card rounded-xl shadow border border-destructive/50">
        <h2 className="text-xl font-bold text-destructive">Access Denied</h2>
        <p className="mt-2 text-muted-foreground">You must be logged in to add a place.</p>
        <button onClick={() => navigate("/places")} className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">Go Back</button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 pb-16">
      <button type="button" onClick={() => navigate("/places")}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition mb-6">
        <ChevronLeft className="h-4 w-4" /> Back to Places
      </button>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Add New Place</h1>
        <p className="text-muted-foreground mt-1">Share a destination with the Uttarakhand community.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Basic Info */}
        <section className="bg-card border border-border rounded-2xl p-6 shadow-sm space-y-5">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest">Basic Info</h2>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Place Name <span className="text-destructive">*</span>
            </label>
            <input type="text" value={form.name} onChange={set("name")} required
              placeholder="e.g. Kedarnath Temple"
              className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Category <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CATEGORIES.map((cat) => (
                <button key={cat.value} type="button"
                  onClick={() => setForm((p) => ({ ...p, category: cat.value }))}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition ${
                    form.category === cat.value
                      ? "bg-primary text-primary-foreground border-primary shadow-sm"
                      : "bg-background text-foreground border-border hover:border-primary/50 hover:bg-muted"
                  }`}
                >
                  {cat.label}
                  {form.category === cat.value && <CheckCircle2 className="h-3.5 w-3.5 ml-auto shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea value={form.description} onChange={set("description")} rows={4}
              placeholder="Tell the community what makes this place special..."
              className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary resize-none transition"
            />
          </div>
        </section>

        {/* Location */}
        <section className="bg-card border border-border rounded-2xl p-6 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest">
            Location <span className="text-destructive">*</span>
          </h2>

          {/* Location Name Input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Location Name
            </label>
            <input
              type="text"
              value={form.address}
              onChange={set("address")}
              placeholder="e.g. Dehradun, Uttarakhand"
              className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
            />
          </div>

          {/* Map */}
          <div className="rounded-xl overflow-hidden shadow-sm relative">
            <GoogleMapComponent
              initialCenter={marker || { lat: 30.3165, lng: 78.0322 }}
              onLocationSelect={(location, addressName) => {
                setMarker(location);
                if (addressName) {
                  setForm(p => ({ ...p, address: addressName }));
                }
              }}
              height="280px"
              showSearch={true}
            />
          </div>

          {marker ? (
            <div className="flex items-center justify-between">
              <p className="text-xs text-primary font-medium">
                ✅ Pinned: {marker.lat.toFixed(5)}, {marker.lng.toFixed(5)}
              </p>
              <button type="button" onClick={() => { setMarker(null); setForm(p => ({...p, address: ""})); }}
                className="text-xs text-destructive hover:text-destructive/80 transition">
                Remove pin
              </button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Select a district or click the map to pin a location</p>
          )}
        </section>

        {/* Photo */}
        <section className="bg-card border border-border rounded-2xl p-6 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest">Photo</h2>

          {preview ? (
            <div className="relative rounded-xl overflow-hidden border border-border">
              <img src={preview} alt="Preview" className="w-full h-52 object-cover" />
              <button type="button" onClick={removeImage}
                className="absolute top-2 right-2 bg-background/90 hover:bg-background text-foreground rounded-full p-1.5 shadow transition">
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                {image?.name}
              </div>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleImage(e.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-xl py-10 cursor-pointer transition ${
                dragOver ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary hover:bg-primary/5"
              }`}
            >
              <div className="h-12 w-12 rounded-full bg-card flex items-center justify-center">
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">
                  Drop an image here, or{" "}
                  <span className="text-primary underline underline-offset-2">browse</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WebP — up to 10MB</p>
              </div>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => handleImage(e.target.files[0])} />
        </section>

        {/* Submit */}
        <div className="flex items-center justify-between gap-3 pt-2">
          <button type="button" onClick={() => navigate("/places")}
            className="px-5 py-2.5 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition">
            Cancel
          </button>
          <button type="submit" disabled={loading}
            className="inline-flex items-center gap-2 px-7 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-primary/90 disabled:opacity-60 transition shadow-sm">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" />Saving...</> : "Add Place"}
          </button>
        </div>
      </form>
    </div>
  );
}
